/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import { formatNumber } from 'humanize-plus';

import * as allDimensions from 'utils/dimensions/definitions';
import { Metric, filter as metricFilter, stringSet, valueSet } from 'utils/metrics/constructors';
import { Query } from 'components/reporting';
import {
  assign,
  filter,
  flatMap,
  flatten,
  forEach,
  keys,
  map,
  range,
  reduce,
  sortBy,
  zip,
  zipWith,
} from 'es-toolkit/compat';
import { dashboard } from 'types';
import { oc } from 'ts-optchain';
import { safeToNumber } from '@tock/coretypes';
import allMetrics from 'utils/metrics';
import protos from 'utils/protos';

const { ReportQuery } = protos;
interface MetricValue {
  v: number;
  f: string;
}

const getMetricName = (metric: any) => {
  if (typeof metric === 'string') {
    return metric;
  } else if (metric.name) {
    return metric.name;
  } else {
    return JSON.stringify(metric.metric);
  }
};

const filterOnBusinessIds = (...businessIds: number[]): dashboard.Filter => {
  return metricFilter({ operation: 'AND', valueSet: [valueSet('BUSINESS_ID', businessIds)] });
};

export const filterOnValueSetsAndBusinessAnd = (valueSets: any, ...businessIds: number[]) => {
  return metricFilter({
    operation: 'AND',
    valueSet: [valueSet('BUSINESS_ID', businessIds)].concat(valueSets || []),
  });
};

export * from 'utils/metrics/constructors';

export function filterOnBusiness(...args: number[]): dashboard.Filter {
  return filterOnBusinessIds(...args);
}

export function filterOnBusinessWithStringColumn(column: any, businessIds: any) {
  return metricFilter({
    operation: 'AND',
    valueSet: [valueSet('BUSINESS_ID', businessIds)],
    filter: [
      metricFilter({
        operation: 'NOT',
        stringSet: [stringSet(column, [''])],
      }),
    ],
  });
}

export function filterOnBusinessWithStringColumnValue(
  column: any,
  values: any,
  businessIds: any
): dashboard.Filter {
  return metricFilter({
    operation: 'AND',
    valueSet: [valueSet('BUSINESS_ID', businessIds)],
    stringSet: [stringSet(column, values)],
  });
}

export function pluckMetric(position: number, table: MetricValue[][][]) {
  return map(table, (row) => {
    return map(row, (col) => {
      if (!col) {
        return {
          f: '0',
          v: 0,
        };
      }
      return col[position];
    });
  });
}

export function splitTable(table: MetricValue[][][], numMetrics: number) {
  return map(range(numMetrics), (position) => {
    return pluckMetric(position, table);
  });
}

export function appendTables(table1: MetricValue[][], table2: MetricValue[][]) {
  return zipWith(table1, table2, (a, b) => a.concat(b));
}

export function unfoldTable(table: MetricValue[][][], numMetrics: number) {
  const splits = splitTable(table, numMetrics);
  return reduce(splits, appendTables);
}

/*
 * Given an array of datapoints, pivots that data into a table where columns are all the
 * dimension values seen (sorted), rows are all timestamps for data seen (sorted), and
 * values are an array consisting of all metrics.
 * @param {{dimensionValues: string[], metricValues: Object[], startInstantMs: Object}[]} datapoints the datapoints to process
 * @returns {{rowLabels: Date[], columnLabels: string[], table: Object[][][]}} pivoted version of the input
 */
export function pivotData(
  datapoints: {
    dimensionValues: dashboard.DimensionValues[];
    metricValues: MetricValue[];
    time: number;
  }[],
  query: Query
) {
  const series = new Map<string, Map<number, MetricValue[]>>();
  // series maps dimension value to map of timestamp to array of metric values. First we
  // populate that map ...
  forEach(datapoints, (datapoint) => {
    const dimensionEmptyValue = query.dimensions?.[0]?.emptyValue ?? '';
    const dimensionString =
      (datapoint.dimensionValues?.[0]?.value ?? []).join(', ') || dimensionEmptyValue;

    const metricValues = datapoint.metricValues;
    if (!series.has(dimensionString)) {
      series.set(dimensionString, new Map<number, MetricValue[]>());
    }
    // In Javascript, two dates that represent the same time are not === to each other, so we
    // can't stash them directly into a set and not get duplicates.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    series.get(dimensionString)!.set(datapoint.time, metricValues);
  });

  // ... then we extract the lists of all timestamps and all dimension values ...
  const dimensionValueSet = new Set<string>();
  const timestampSet = new Set<number>();
  series.forEach((reportDataMap, dimensionValue) => {
    dimensionValueSet.add(dimensionValue);
    reportDataMap.forEach((metrics, timestamp) => {
      timestampSet.add(timestamp);
    });
  });
  const timestamps = sortBy(Array.from(timestampSet));
  const dimensionValues = Array.from(dimensionValueSet);
  // ... and finally we build a table of all timestamps as rows and all dimension values as columns.
  const pivotedDataList = map(timestamps, (timestamp) => {
    return map(dimensionValues, (dimensionValue) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return series.get(dimensionValue)!.get(timestamp)!;
    });
  });
  return {
    rowLabels: timestamps,
    columnLabels: dimensionValues,
    table: pivotedDataList,
  };
}

export function assembleChartData(columnLabels: string[], rowLabels: number[], unfolded: any) {
  const data = zipWith(rowLabels, unfolded, (label, row: any) => [label].concat(row));
  return {
    columnLabels,
    data,
  };
}

export function readQueryResponsesToReportData(
  datapointsInput: dashboard.Datapoint[],
  query: Query,
  initialSort?: (row: dashboard.IDatapoint) => number | string
) {
  let datapoints = datapointsInput;
  if (datapoints.length > 0 && oc(datapoints)[0].dimensionValue.value([]).length > 0) {
    let sortFunction = (point: dashboard.Datapoint): string | number =>
      oc(datapoints)[0].dimensionValue.value([]).join(',');
    if (initialSort) {
      sortFunction = initialSort;
    }
    datapoints = sortBy(datapoints, sortFunction);
  }
  const unpivoted = map(datapoints, (datapoint: dashboard.Datapoint) => {
    const dimensionValues = oc(datapoint).dimensionValue.value() as dashboard.DimensionValues[];
    const metricValues = map(
      filter(zip(query.metrics, datapoint.metricValue)) as [Metric, dashboard.INumber][],
      (metricEntry: [Metric, dashboard.INumber]): MetricValue => {
        const metric = metricEntry[0];
        const value = safeToNumber(metricEntry[1].value) ?? 0;
        if (metric.formatter) {
          return {
            v: value,
            f: metric.formatter(value),
          };
        }
        return {
          v: value,
          f: formatNumber(value, 0),
        };
      }
    );
    return {
      dimensionValues,
      metricValues,
      time: safeToNumber(datapoint.startInstantMs),
    };
  });
  const pivoted = pivotData(unpivoted, query);
  const unfolded = unfoldTable(pivoted.table, query.metrics.length);
  const perMetricColumnLabels: string[] = flatten(
    flatMap(query.metrics, (metric) => {
      return map(pivoted.columnLabels, (label) => {
        if (label === '') {
          return getMetricName(metric);
        } else if (query.metrics.length < 2) {
          // If there is only one metric, don't clutter the legend with its name
          return label;
        } else {
          return label + ': ' + getMetricName(metric);
        }
      });
    })
  );
  return assembleChartData(perMetricColumnLabels, pivoted.rowLabels, unfolded);
}

export function toReportData(
  datapoints: dashboard.Datapoint[],
  query: Query,
  initialSort?: (row: dashboard.IDatapoint) => number | string
) {
  const response = readQueryResponsesToReportData(datapoints, query, initialSort);
  const bucketingAll = query.bucketing === 'ALL';
  let chartSeries: any[] = [];
  let data: any[] = [];
  if (bucketingAll) {
    data = response.columnLabels.map((metric: any, idx: any) => {
      return {
        ' ': (response.data[0][idx + 1] as any).v,
        x: metric,
      };
    });
    chartSeries = [
      {
        field: ' ',
        area: true,
      },
    ];
    return {
      data,
      chartSeries,
    };
  } else {
    data = response.data
      .map((point) => {
        const entry: any = {
          x: point[0],
        };
        response.columnLabels.forEach((metric: any, idx: any) => {
          entry[metric] = (point[idx + 1] as any).v;
        });
        return entry;
      })
      .filter((entry: any) => {
        return keys(entry).length === response.columnLabels.length + 1;
      });
    chartSeries = response.columnLabels.map((metric: any) => {
      return {
        field: metric,
        area: true,
      };
    });
    return {
      data,
      chartSeries,
    };
  }
}

export const colorList = [
  '#3B82F6',
  '#EC4899',
  '#22C55E',
  '#6366F1',
  '#EF4444',
  '#EAB308',
  '#A855F7',
  '#B47C49',
  '#6B7280',
  '#14B8A6',
  '#F97316',
];

export const metrics = {
  ...allMetrics,
};

export const dimensions = {
  ...allDimensions,
};

export function metricWithName(metricInput: any, name: any, desc?: any) {
  if (!metricInput || !metricInput.name) {
    console.error('Encountered invalid metric', metricInput);
  }
  const metric = assign({}, metricInput);
  metric.name = name;
  if (desc) {
    metric.desc = desc;
  }
  return metric;
}

export function hiddenIfZeroMetric(metricInput: any) {
  if (!metricInput || !metricInput.name) {
    console.error('Encountered invalid metric', metricInput);
  }
  const metric = assign({}, metricInput);
  metric.hideIfZero = true;
  return metric;
}

export function highlightMetric(metricInput: any, doubleLine?: any) {
  if (!metricInput || !metricInput.name) {
    console.error('Encountered invalid metric', metricInput);
  }
  const metric = assign({}, metricInput);
  metric.highlight = true;
  if (doubleLine) {
    metric.doubleLine = true;
  }
  return metric;
}

export function subMetric(metricInput: any) {
  if (!metricInput || !metricInput.name) {
    console.error('Encountered invalid metric', metricInput);
  }
  const metric = assign({}, metricInput);
  metric.subMetric = true;
  return metric;
}

export function negativeMetric(metricInput: any) {
  if (!metricInput || !metricInput.name) {
    console.error('Encountered invalid metric', metricInput);
  }
  const metric = assign({}, metricInput);
  metric.negative = true;
  return metric;
}

export function topPadded(metricInput: any) {
  if (!metricInput || !metricInput.name) {
    console.error('Encountered invalid metric', metricInput);
  }
  const metric = assign({}, metricInput);
  metric.topPadded = true;
  return metric;
}

export function getMetricClassName(metric: Metric): string | null {
  if (
    metric.type === 'currency' ||
    metric.type === 'duration' ||
    metric.type === 'number' ||
    metric.type === 'percentCents'
  ) {
    return 'GenericTable-cell--numeric';
  }

  return null;
}

export function notCancelled(metric: Metric): Metric {
  metric.metric.filter = {
    operation: 'AND',
    valueSet: [
      {
        field: ReportQuery.Dimension.IS_CANCELLED,
        value: [0],
      },
    ],
  };
  return metric;
}
